import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../../firebase/config';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';

const AdminCompanias = () => {
    const navigate = useNavigate();
    const [companias, setCompanias] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterGroup, setFilterGroup] = useState('PATRIMONIALES'); 

    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        grupo: 'PATRIMONIALES',
        codigos: [] 
    });

    const companiasRef = collection(db, 'public/data/companias');

    const fetchData = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(companiasRef);
            const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            data.sort((a, b) => a.nombre.localeCompare(b.nombre));
            setCompanias(data);
        } catch (error) {
            console.error("Error cargando compañías:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const handleOpenModal = (cia = null) => {
        if (cia) {
            setFormData({ 
                ...cia, 
                grupo: cia.grupo || 'PATRIMONIALES',
                codigos: Array.isArray(cia.codigos) ? cia.codigos : [] 
            });
            setIsEditing(cia.id);
        } else {
            setFormData({ 
                nombre: '', 
                grupo: filterGroup, 
                codigos: [{ organizador: '', pas: '' }] 
            });
            setIsEditing(null);
        }
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const codigosLimpios = formData.codigos.filter(c => c.organizador.trim() !== '' || c.pas.trim() !== '');
            const payload = { 
                ...formData, 
                nombre: formData.nombre.toUpperCase(),
                codigos: codigosLimpios,
                grupo: formData.grupo 
            };

            if (isEditing) await updateDoc(doc(db, 'public/data/companias', isEditing), payload);
            else await addDoc(companiasRef, payload);
            
            setShowModal(false);
            fetchData();
        } catch (error) {
            alert("Error al guardar compañía");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Eliminar esta compañía?")) {
            await deleteDoc(doc(db, 'public/data/companias', id));
            fetchData();
        }
    };

    const handleCodigoChange = (index, field, value) => {
        const newCodigos = [...formData.codigos];
        newCodigos[index][field] = value;
        setFormData({ ...formData, codigos: newCodigos });
    };

    const addCodigo = () => {
        setFormData({ ...formData, codigos: [...formData.codigos, { organizador: '', pas: '' }] });
    };

    const removeCodigo = (index) => {
        const newCodigos = formData.codigos.filter((_, i) => i !== index);
        setFormData({ ...formData, codigos: newCodigos });
    };

    const listaFiltrada = companias.filter(c => c.grupo === filterGroup);

    return (
        <div className="p-8 bg-gray-50 min-h-screen font-sans text-gray-800">
            <div className="max-w-6xl mx-auto">
                
                {/* HEADER */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Compañías y Códigos</h1>
                        <p className="text-gray-500 mt-1">Gestión de aseguradoras y códigos de producción.</p>
                    </div>
                    
                    {/* TABS */}
                    <div className="flex bg-white p-1 rounded-xl shadow-sm border border-gray-200">
                        <button 
                            onClick={() => setFilterGroup('PATRIMONIALES')}
                            className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${filterGroup === 'PATRIMONIALES' ? 'bg-teal-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`}
                        >
                            Patrimoniales
                        </button>
                        <button 
                            onClick={() => setFilterGroup('ART')}
                            className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${filterGroup === 'ART' ? 'bg-teal-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`}
                        >
                            Riesgos del Trabajo (ART)
                        </button>
                    </div>

                    <button onClick={() => navigate('/AdminDashboard')} className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg font-bold shadow-sm hover:bg-gray-50 transition text-sm">
                        Volver
                    </button>
                </div>

                {/* BOTÓN NUEVA COMPAÑÍA */}
                <div className="flex justify-end mb-4">
                    <button onClick={() => handleOpenModal()} className="bg-teal-600 text-white px-5 py-2.5 rounded-lg font-bold shadow hover:bg-teal-700 text-sm flex gap-2 items-center transition-all hover:scale-105">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
                        Nueva Compañía
                    </button>
                </div>

                {/* TABLA ESTILIZADA */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 text-gray-500 uppercase font-bold text-xs tracking-wider border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4 w-1/4">Compañía</th>
                                <th className="px-6 py-4 w-1/6">Grupo</th>
                                <th className="px-6 py-4">Códigos Asignados</th>
                                <th className="px-6 py-4 text-center w-1/6">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-sm">
                            {loading ? (
                                <tr><td colSpan="4" className="text-center py-8">Cargando...</td></tr>
                            ) : listaFiltrada.length === 0 ? (
                                <tr><td colSpan="4" className="text-center py-8 text-gray-400">No hay compañías en este grupo.</td></tr>
                            ) : (
                                listaFiltrada.map(cia => (
                                    <tr key={cia.id} className="hover:bg-teal-50/20 transition-colors">
                                        <td className="px-6 py-4 align-top">
                                            <div className="font-bold text-teal-800 text-base">{cia.nombre}</div>
                                        </td>
                                        <td className="px-6 py-4 align-top">
                                            <span className={`text-[10px] font-bold px-2 py-1 rounded border uppercase tracking-wide ${cia.grupo === 'ART' ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                                {cia.grupo}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 align-top">
                                            {cia.codigos && cia.codigos.length > 0 ? (
                                                <div className="flex flex-col gap-1">
                                                    {cia.codigos.map((cod, i) => (
                                                        <div key={i} className="text-xs text-gray-600 flex items-center bg-gray-50 px-3 py-1.5 rounded border border-gray-100 w-fit">
                                                            <span className="font-bold text-gray-400 text-[10px] mr-1.5">ORG:</span> 
                                                            <span className="font-semibold text-gray-800 mr-3">{cod.organizador || '-'}</span>
                                                            <span className="font-bold text-gray-400 text-[10px] mr-1.5">PAS:</span> 
                                                            <span className="font-semibold text-teal-700">{cod.pas || '-'}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : <span className="text-gray-300 italic text-xs">Sin códigos</span>}
                                        </td>
                                        <td className="px-6 py-4 text-center align-middle">
                                            <div className="flex justify-center gap-2">
                                                <button onClick={() => handleOpenModal(cia)} className="text-gray-400 hover:text-teal-600 p-2 rounded-lg hover:bg-teal-50 transition" title="Editar"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536l12.232-12.232z"></path></svg></button>
                                                <button onClick={() => handleDelete(cia.id)} className="text-gray-400 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition" title="Eliminar"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

            </div>

            {/* MODAL */}
            {showModal && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-fade-in flex flex-col max-h-[90vh] border border-gray-100">
                        <div className="bg-teal-600 px-6 py-4 flex justify-between items-center text-white flex-shrink-0">
                            <h3 className="font-bold text-lg">{isEditing ? 'Editar Compañía' : 'Nueva Compañía'}</h3>
                            <button onClick={() => setShowModal(false)} className="hover:text-gray-200 text-white font-bold text-xl">&times;</button>
                        </div>
                        
                        {/* CORRECCIÓN: Se agrega id="companiaForm" para vincularlo con el botón de abajo */}
                        <form id="companiaForm" onSubmit={handleSave} className="p-6 space-y-6 overflow-y-auto bg-gray-50 flex-grow">
                            <div className="grid grid-cols-2 gap-5 mb-6">
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Nombre</label>
                                    <input 
                                        type="text" 
                                        value={formData.nombre} 
                                        onChange={(e) => setFormData({...formData, nombre: e.target.value})} 
                                        className="w-full border border-gray-300 p-3 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none uppercase font-medium bg-white" 
                                        required 
                                        placeholder="EJ: ALLIANZ"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide block mb-1">Grupo</label>
                                    <select 
                                        value={formData.grupo} 
                                        onChange={(e) => setFormData({...formData, grupo: e.target.value})} 
                                        className="w-full border border-gray-300 p-3 rounded-xl focus:ring-2 focus:ring-teal-500 outline-none bg-white transition cursor-pointer"
                                    >
                                        <option value="PATRIMONIALES">PATRIMONIALES</option>
                                        <option value="ART">ART</option>
                                    </select>
                                </div>
                            </div>

                            <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm">
                                <div className="flex justify-between items-center mb-4">
                                    <h4 className="text-sm font-bold text-gray-700">Códigos (Organizador / PAS)</h4>
                                    <button type="button" onClick={addCodigo} className="text-xs bg-teal-50 text-teal-800 border border-teal-200 px-3 py-1.5 rounded-lg font-bold hover:bg-teal-100 transition shadow-sm">+ Agregar Código</button>
                                </div>
                                
                                <div className="space-y-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                                    {formData.codigos.map((cod, idx) => (
                                        <div key={idx} className="flex gap-3 items-center bg-gray-50 p-3 rounded-xl border border-gray-100">
                                            <div className="flex-grow grid grid-cols-2 gap-3">
                                                <div>
                                                    <label className="text-[10px] text-gray-400 block mb-0.5 uppercase font-bold">Organizador</label>
                                                    <input 
                                                        type="text" 
                                                        placeholder="Ej: CIO M9536" 
                                                        value={cod.organizador} 
                                                        onChange={(e) => handleCodigoChange(idx, 'organizador', e.target.value)}
                                                        className="w-full border border-gray-200 rounded-lg p-2 text-sm outline-none focus:border-teal-400 focus:bg-white transition"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-gray-400 block mb-0.5 uppercase font-bold">PAS</label>
                                                    <input 
                                                        type="text" 
                                                        placeholder="Ej: Marcos M5706" 
                                                        value={cod.pas} 
                                                        onChange={(e) => handleCodigoChange(idx, 'pas', e.target.value)}
                                                        className="w-full border border-gray-200 rounded-lg p-2 text-sm outline-none focus:border-teal-400 focus:bg-white transition"
                                                    />
                                                </div>
                                            </div>
                                            <button type="button" onClick={() => removeCodigo(idx)} className="text-gray-400 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition self-center mt-3" title="Eliminar Código">✕</button>
                                        </div>
                                    ))}
                                    {formData.codigos.length === 0 && <p className="text-xs text-gray-400 italic text-center py-4 border-2 border-dashed border-gray-100 rounded-xl">Sin códigos asignados.</p>}
                                </div>
                            </div>
                        </form>

                        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-white flex-shrink-0">
                            <button type="button" onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-lg border border-gray-300 text-gray-600 font-bold hover:bg-gray-50 transition">Cancelar</button>
                            {/* Este botón dispara el formulario con id="companiaForm" */}
                            <button type="submit" form="companiaForm" className="px-8 py-2.5 rounded-lg bg-teal-600 text-white font-bold hover:bg-teal-700 shadow-lg transition transform hover:scale-[1.01]">Guardar</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminCompanias;